// The LEFT panel's Elements mode (TODO §21/§7) — the palette of shapes you can drop into
// the diagram being edited.
//
// §7.1 It is a TAB SWITCH on the existing nav panel, never a replacement. The panel already
// has a two-way view toggle (thumbnails / outline); this adds a mode, auto-selected on
// entering diagram edit and restored on exit, with slide selection and scroll position
// surviving the round trip. A third panel would not fit — the deck's own column is
// `minSize={480}` — and a floating palette over a canvas someone is drawing on is the worst
// of both.

import { useMemo, useState } from "react";
import { Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import { useDiagramEditing } from "@/components/deck/diagramEditing";
import { loadDiagramEngine } from "@/lib/diagram-engine";
import { filterStencils, type Stencil } from "@/lib/diagramStencils";
import { cn } from "@/lib/utils";

// The drag payload. A plain string on a custom MIME type, so a drag that lands anywhere
// other than the canvas is simply ignored by whatever is under it.
const DRAG_TYPE = "application/x-slidecraft-stencil";

export function DiagramPalette() {
  const { api, editingDocId } = useDiagramEditing();
  const [query, setQuery] = useState("");
  const stencils = useMemo(() => filterStencils(query), [query]);

  // §7.5 A plain CLICK means draw-to-place: set the active tool and let the author drag out
  // the shape on the canvas, which is what people expect from a shape palette. A stencil with
  // no single-primitive equivalent (the cylinder, the actor) has no tool, so a click on it
  // drops it at the centre of the visible canvas instead.
  const onClick = async (stencil: Stencil) => {
    if (!api) return;
    if (stencil.tool) {
      api.setActiveTool({ type: stencil.tool });
      return;
    }
    await dropAt(stencil, null);
  };

  // §7.5 Drop mechanics: map the pointer to scene coordinates with
  // `viewportCoordsToSceneCoords`, build real elements with `convertToExcalidrawElements`,
  // and commit with `CaptureUpdateAction.IMMEDIATELY` so the whole drop is ONE undo step.
  const dropAt = async (
    stencil: Stencil,
    pointer: { clientX: number; clientY: number } | null,
  ) => {
    if (!api) return;
    const engine = await loadDiagramEngine();
    const appState = api.getAppState();
    const scene = pointer
      ? engine.viewportCoordsToSceneCoords(
          { clientX: pointer.clientX, clientY: pointer.clientY },
          appState,
        )
      : // No pointer (a click on a tool-less stencil): the centre of what is on screen.
        engine.viewportCoordsToSceneCoords(
          {
            clientX: appState.offsetLeft + appState.width / 2,
            clientY: appState.offsetTop + appState.height / 2,
          },
          appState,
        );
    // Centre the shape on the drop point rather than hanging it down-right of the cursor.
    const built = engine.convertToExcalidrawElements(
      stencil.build(
        Math.round(scene.x - stencil.width / 2),
        Math.round(scene.y - stencil.height / 2),
      ) as never,
    );
    api.updateScene({
      elements: [...api.getSceneElements(), ...built],
      captureUpdate: engine.CaptureUpdateAction.IMMEDIATELY,
    });
  };

  if (!editingDocId) {
    return (
      <div data-diagram-ui className="p-4 text-sm text-slate-500">
        Double-click a diagram on the slide to edit it — the elements you can
        drop in appear here.
      </div>
    );
  }

  return (
    // §6.4 Part of the editing session, not "outside" it: a click on a stencil must not be
    // the click that closes the editor it is meant for.
    <div data-diagram-ui className="flex min-h-0 flex-1 flex-col">
      <div className="relative shrink-0 px-2 py-2">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search elements"
          className="h-8 pl-7 text-sm"
          aria-label="Search elements"
        />
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-2 content-start gap-2 overflow-y-auto p-2">
        {stencils.map((stencil) => (
          <button
            key={stencil.id}
            type="button"
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(DRAG_TYPE, stencil.id);
              e.dataTransfer.effectAllowed = "copy";
            }}
            onDragEnd={(e) => {
              // The canvas is a sibling subtree with its own pointer handling, so rather
              // than fighting it for a `drop` listener the placement is done here, from the
              // drag's own end coordinates. `dragend` fires on the SOURCE element and
              // carries the final pointer position, which is all `dropAt` needs.
              if (e.clientX === 0 && e.clientY === 0) return; // cancelled drag
              void dropAt(stencil, { clientX: e.clientX, clientY: e.clientY });
            }}
            onClick={() => void onClick(stencil)}
            title={
              stencil.tool
                ? `${stencil.name} — click to draw, or drag onto the canvas`
                : `${stencil.name} — click to place, or drag onto the canvas`
            }
            className={cn(
              "flex flex-col items-center gap-1 rounded-md border border-slate-200 bg-white p-2",
              "hover:border-slate-400 hover:bg-slate-50",
            )}
          >
            <StencilGlyph id={stencil.id} />
            <span className="w-full truncate text-center text-[11px] text-slate-600">
              {stencil.name}
            </span>
          </button>
        ))}
        {stencils.length === 0 && (
          <p className="col-span-2 p-2 text-center text-xs text-slate-400">
            No elements match “{query}”.
          </p>
        )}
      </div>
    </div>
  );
}

// A tiny inline preview per stencil. Hand-drawn SVG rather than a render of the stencil
// itself: rendering the real thing would mean loading the Excalidraw chunk to draw a 36px
// tile, which is precisely what §10.3 exists to avoid.
function StencilGlyph({ id }: { id: string }) {
  const stroke = "#334155";
  const common = {
    fill: "none",
    stroke,
    strokeWidth: 1.5,
    strokeLinejoin: "round" as const,
    strokeLinecap: "round" as const,
  };
  return (
    <svg viewBox="0 0 48 30" className="h-7 w-full" aria-hidden="true">
      {id === "box" && <rect x="8" y="7" width="32" height="16" {...common} />}
      {id === "rounded-box" && (
        <rect x="8" y="7" width="32" height="16" rx="6" {...common} />
      )}
      {id === "decision" && <path d="M24 4 38 15 24 26 10 15Z" {...common} />}
      {id === "cylinder" && (
        <>
          <ellipse cx="24" cy="9" rx="12" ry="4" {...common} />
          <path d="M12 9v12c0 2.2 5.4 4 12 4s12-1.8 12-4V9" {...common} />
        </>
      )}
      {id === "actor" && (
        <>
          <circle cx="24" cy="7" r="4" {...common} />
          <path d="M24 11v9M17 15h14M24 20l-5 6M24 20l5 6" {...common} />
        </>
      )}
      {id === "chevron" && <path d="M8 7h26l6 8-6 8H8l6-8Z" {...common} />}
      {id === "callout" && (
        <path d="M8 5h32v14H22l-6 6v-6H8Z" rx="4" {...common} />
      )}
      {id === "swimlane" && (
        <>
          <rect x="5" y="8" width="38" height="14" {...common} />
          <path d="M17 8v14" {...common} />
        </>
      )}
      {id === "arrow" && <path d="M8 15h30m0 0-6-5m6 5-6 5" {...common} />}
      {id === "bracket" && <path d="M32 4h-8v22h8" {...common} />}
      {id === "text" && <path d="M12 9h24M24 9v14M18 23h12" {...common} />}
    </svg>
  );
}
